import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { ref, createRef } from 'lit/directives/ref.js';
import styles from './card-drawer.scss?inline';

export default class SotbitCardDrawer extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        _hasFooter: { type: Boolean, state: true },
        isOpen: { type: Boolean, attribute: 'is-open', reflect: true },
        isLoading: { type: Boolean, attribute: 'is-loading', reflect: true },
    };

    constructor() {
        super();

        this.drawerConstsCSS = {
            OVERLAY: 'b2b-drawer-overlay',
            CONTAINER: 'b2b-drawer-container',
            OPEN: 'is-open',
            POSITION_PREFIX: 'b2b-drawer--',
        };

        this.overlay = null;
        this.drawerContainer = null;
        this.container = createRef();
        this.content = createRef();
        this._hasFooter = false;
        this._footerRef = createRef();
        this._resizeObser = null;
        this.isOpen = false;
    }

    firstUpdated() {
        this.originalParent = this.parentElement;
    }

    connectedCallback() {
        super.connectedCallback();

        setTimeout(() => {
            this._observeFooterHeight();
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObser?.disconnect();
        this._resizeObser = null;
    }

    _handleFooterSlotChange(e) {
        const childNodes = e.target.assignedNodes();
        this._hasFooter = childNodes.length > 0;
    }

    _observeFooterHeight() {
        if (!this._footerRef?.value) {
            return;
        }

        const variableName = '--footer-height';

        this._resizeObser = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const height = Math.ceil(entry.contentRect.height + entry.contentRect.y * 2);

                this.style.setProperty(variableName, `${height}px`);
            }
        });

        this._resizeObser.observe(this._footerRef?.value);

        const initialHeight = this._footerRef?.value.getBoundingClientRect().height;
        this.style.setProperty(variableName, `${initialHeight}px`);
    }

    close() {
        if (!this.drawerContainer) {
            return;
        }

        this.isOpen = false;

        this.dispatchEvent(
            new CustomEvent('beforeclose', {
                bubbles: true,
                composed: true,
            })
        );

        this.drawerContainer.classList.remove(this.drawerConstsCSS.OPEN);

        if (this.overlay) {
            this.overlay.classList.remove(this.drawerConstsCSS.OPEN);
        }
        document.body.classList.remove('fixed');

        this.drawerContainer.addEventListener(
            'transitionend',
            () => {
                this.originalParent.appendChild(this);
                this.drawerContainer.remove();
                this.remove();

                if (this.overlay) {
                    this.overlay.remove();
                }

                this.dispatchEvent(
                    new CustomEvent('close', {
                        bubbles: true,
                        composed: true,
                    })
                );
            },
            { once: true }
        );
    }

    open(options = {}) {
        this.isOpen = true;
        const DEFAULT_POSITION = 'right';
        const DISPLAY_NONE = 'd-none';

        const config = {
            position: DEFAULT_POSITION,
            hideClass: DISPLAY_NONE,
            ...options,
        };
        const wasHidden = this.classList.contains(config.hideClass);

        if (wasHidden) {
            this.classList.remove(config.hideClass);
        }

        this.overlay = document.createElement('div');
        this.overlay.className = this.drawerConstsCSS.OVERLAY;
        this.overlay.addEventListener('click', this.close.bind(this));

        this.drawerContainer = document.createElement('div');
        this.drawerContainer.className = `${this.drawerConstsCSS.CONTAINER} ${this.drawerConstsCSS.POSITION_PREFIX}${config.position}`;

        if (config.classes?.drawer) {
            this.drawerContainer.classList.add(config.classes.drawer);
        }

        this.drawerContainer.appendChild(this);

        document.body.appendChild(this.overlay);
        document.body.appendChild(this.drawerContainer);
        document.body.classList.add('fixed');

        this.dispatchEvent(
            new CustomEvent('open', {
                bubbles: true,
                composed: true,
            })
        );

        requestAnimationFrame(() => {
            this.overlay.classList.add(this.drawerConstsCSS.OPEN);
            this.drawerContainer.classList.add(this.drawerConstsCSS.OPEN);
        });

        return { overlay: this.overlay };
    }

    render() {
        return html`
            <div class="sotbit-cabinet-drawer" ${ref(this.container)}>
                <div class="wrapper" part="wrapper">
                    <div class="header" part="header">
                        <span class="title">
                            <slot name="title"></slot>
                        </span>
                        <slot name="close">
                            <sotbit-icon
                                    slot="close"
                                    @click="${this.close}"
                                    size="m"
                                    icon="close"
                                    variant="secondary"
                                    pointer
                            >
                        </slot>
                    </div>
                    <div part="container--outer" class="container--outer">
                        <div part="container" class="container">
                            <slot name="content"></slot>
                        </div>
                    </div>
                    <div part="footer${!this._hasFooter ? ' footer-hidden' : ''}"
                         class="footer" ?hidden="${!this._hasFooter}" ${ref(this._footerRef)}>
                        <slot @slotchange="${this._handleFooterSlotChange}" name="footer"></slot>
                    </div>
                </div>
                ${this.isLoading ? html`<div part="loader-overlay"><sotbit-loader size="xl"></sotbit-loader></div>` : nothing}
            </div>
        `;
    }
}
